import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { LocaleTypes, PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ModalTypes } from "@enums/modal-types";
import {
    FormattedRpServiceConfigurationAttribute,
    FormattedRpServiceConfigurationPolicyConfig,
    FormattedRpServiceConfigurationPolicyConfigDecision,
    FormattedRpServiceConfigurationProperty,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import { AxnAttributeType, AxnAttributeTypeField, AxnCompany } from "@idw-types/data/reference-data/axn";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { AddRpServiceConfigurationPolicyConfigDecisionModal } from "@containers/modals/add-rp-service-configuration-policy-config-decision-modal/add-rp-service-configuration-policy-config-decision-modal";
import { DefaultDecisionContainer } from "./default-decision-container/default-decision-container";
import { getActiveAcquiredAttributes } from "@utils/policies/acquired-attribute-util";
import { DecisionEngineDecisionsContainer } from "./decision-engine-decisions-container/decision-engine-decisions-container";
import commonStyles from "../../../verification-service-details-component.less";
import styles from "./decision-engine-policy-editor-container.less";
import { AvailableAssertionStatus } from "@idw-types/data/reference-data/generic";
import { AXNUI_CONTENT_PROPERTY_TYPE_ID } from "@constants/verification-service-details-constants";
import { AXNUIContent, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { getUpdatedPropertiesForContentPropertyUpdate } from "@utils/content-util";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";

type DecisionEnginePolicyEditorContainerProps = {
    isDisabled: boolean;
    isUsingThemedUI: boolean;
    axnCompanies: Array<AxnCompany>;
    axnRpServices: Array<AxnRpService>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    policyConfig: FormattedRpServiceConfigurationPolicyConfig;
    attributes: Array<FormattedRpServiceConfigurationAttribute>;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    axnAcquiredAttributes: Array<AxnAcquiredAttribute>;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    properties: Array<FormattedRpServiceConfigurationProperty>;
    validationErrors: PolicyDetailErrors;
    selectedLanguage: LocaleTypes;
    axnuiContent: AXNUIContent;
    availableLanguages: Array<LocaleTypes>;
    isMultiLanguageEnabled: boolean;
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>;
    rpServiceID: string;
    workflowID: string;
    updatePropertiesAndPolicyConfig: (properties: Array<FormattedRpServiceConfigurationProperty>, policyConfig: FormattedRpServiceConfigurationPolicyConfig)  => void;
    updateProperties: (properties: Array<FormattedRpServiceConfigurationProperty>) => void;
    updatePolicyConfig: (policyConfig: FormattedRpServiceConfigurationPolicyConfig) => void;
    
};

export const DecisionEnginePolicyEditorContainer = (props: DecisionEnginePolicyEditorContainerProps) => {
    const {
        isDisabled,
        isUsingThemedUI,
        axnRpServices,
        axnCompanies,
        axnAttributeTypes,
        axnAttributeTypeFields,
        validationErrors,
        policyConfig,
        properties,
        selectedLanguage,
        availableLanguages,
        isMultiLanguageEnabled,
        multiLanguageAXNUIContent,
        attributes,
        axnuiContent,
        serviceOfferings,
        axnAcquiredAttributes,
        simpleRPServiceWorkflows,
        rpServiceID,
        workflowID,
        axnAvailableAssertionStatuses,
        updatePropertiesAndPolicyConfig,
        updateProperties,
        updatePolicyConfig
    } = props;

    const modalContext = useModalContext();

    const acquiredAttributes = useMemo(() => {
        return getActiveAcquiredAttributes(serviceOfferings, attributes, axnAcquiredAttributes);
    }, [serviceOfferings, attributes]);

    const updateAXNUIContentPropertyValue = (newAXNUIContent: AXNUIContent) => {
        const newProperties = getUpdatedPropertiesForContentPropertyUpdate(
            AXNUI_CONTENT_PROPERTY_TYPE_ID,
            properties,
            newAXNUIContent,
            multiLanguageAXNUIContent,
            availableLanguages,
            selectedLanguage,
            isMultiLanguageEnabled,
            DEFAULT_AXNUI_CONTENT
        );
        updateProperties(newProperties);
    };


    const addRpServiceConfigurationPolicyConfigDecision = (decisionToBeAdded: FormattedRpServiceConfigurationPolicyConfigDecision , newAXNUIContent: AXNUIContent) => {
        const newDecisions = [...policyConfig.decisions, decisionToBeAdded];
        const newPolicyConfig = {
            ...policyConfig,
            decisions: newDecisions
        };
        setupPropAndPolicyUpdate(newPolicyConfig, newAXNUIContent);
    };

    const setupPropAndPolicyUpdate = (newPolicyConfig: FormattedRpServiceConfigurationPolicyConfig , newAXNUIContent: AXNUIContent) => {
        const newProperties = getUpdatedPropertiesForContentPropertyUpdate(
            AXNUI_CONTENT_PROPERTY_TYPE_ID,
            properties,
            newAXNUIContent,
            multiLanguageAXNUIContent,
            availableLanguages,
            selectedLanguage,
            isMultiLanguageEnabled,
            DEFAULT_AXNUI_CONTENT
        );
        updatePropertiesAndPolicyConfig(newProperties, newPolicyConfig);
    };


    

    const onClickAddRule = () => {
        const renderAddRpServiceConfigurationPolicyDecisionModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationPolicyConfigDecisionModal
                    axnRpServices={axnRpServices}
                    isUsingThemedUI={isUsingThemedUI}
                    attributes={attributes}
                    axnCompanies={axnCompanies}
                    axnuiContent={axnuiContent}
                    validationErrors={validationErrors}
                    acquiredAttributes={acquiredAttributes}
                    axnAttributeTypes={axnAttributeTypes}
                    axnAttributeTypeFields={axnAttributeTypeFields}
                    unformattedAcquiredAttributes={axnAcquiredAttributes}
                    serviceOfferings={serviceOfferings}
                    policyRetries={policyConfig.policyRetries}
                    addRpServiceConfigurationPolicyConfigDecision={addRpServiceConfigurationPolicyConfigDecision}
                    simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                    axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                    rpServiceID={rpServiceID}
                    workflowID={workflowID}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddRpServiceConfigurationPolicyDecisionModal,
            modalType: ModalTypes.AddRule
        });
    };

    const rulesHeaderButton = (
        (!isDisabled) ? (
            <Button id="decision-engine-add-rule" onClick={onClickAddRule}>
                {resources.pages.verificationServiceDetails.tabs.policies.sections.editor.sections.rules.buttons.addRule.label}
            </Button>
        ) : null
    );

    const { denyPolicyDecisions, approvePolicyDecisions, obligatePolicyDecisions } = useMemo(() => {
        const activePD = policyConfig.decisions.filter(decision => decision.status === EntityStatusTypes.ACTIVE);

        return {
            denyPolicyDecisions: activePD.filter(decision => decision.policyDecisionType === PolicyDecisionActionTypes.DENY),
            approvePolicyDecisions: activePD.filter(decision => decision.policyDecisionType === PolicyDecisionActionTypes.APPROVE),
            obligatePolicyDecisions: activePD.filter(decision => decision.policyDecisionType === PolicyDecisionActionTypes.OBLIGATION),
        };
    }, [policyConfig.decisions]);

    //todo - need to handle disposition
    return (
        <div className={styles.decisionEnginePolicyEditorContainer}>
            <span className={commonStyles.description}>{resources.pages.verificationServiceDetails.tabs.policies.sections.editor.description}</span>
            <CardContainer className={commonStyles.greyBackground}>
                <div>
                    <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.policies.sections.editor.sections.rules.title} headerButton={rulesHeaderButton} />
                    <ContentPageBody isMaxWidth={true}>
                        <span className={commonStyles.description}>{resources.pages.verificationServiceDetails.tabs.policies.sections.editor.sections.rules.description}</span>
                        {(denyPolicyDecisions.length === 0 && approvePolicyDecisions.length === 0 && obligatePolicyDecisions.length === 0) && (
                            <Alert variant={"warning"}>{resources.pages.verificationServiceDetails.tabs.policies.sections.editor.sections.rules.noRulesMessage(isDisabled)}</Alert>
                        )}
                        <DecisionEngineDecisionsContainer
                            sectionTitle={"Deny Rules"}
                            decisionType={PolicyDecisionActionTypes.DENY}
                            isDisabled={isDisabled}
                            isUsingThemedUI={isUsingThemedUI}
                            axnRpServices={axnRpServices}
                            validationErrors={validationErrors}
                            axnuiContent={axnuiContent}
                            axnCompanies={axnCompanies}
                            axnAttributeTypes={axnAttributeTypes}
                            axnAttributeTypeFields={axnAttributeTypeFields}
                            policyConfig={policyConfig}
                            policyDecisions={denyPolicyDecisions}
                            attributes={attributes}
                            serviceOfferings={serviceOfferings}
                            acquiredAttributes={acquiredAttributes}
                            unformattedAcquiredAttributes={axnAcquiredAttributes}
                            simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                            axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                            rpServiceID={rpServiceID}
                            workflowID={workflowID}
                            setupPropAndPolicyUpdate={setupPropAndPolicyUpdate}
                            updatePolicyConfig={updatePolicyConfig}
                        />
                        <DecisionEngineDecisionsContainer
                            sectionTitle={"Approve Rules"}
                            decisionType={PolicyDecisionActionTypes.APPROVE}
                            isDisabled={isDisabled}
                            isUsingThemedUI={isUsingThemedUI}
                            axnuiContent={axnuiContent}
                            axnRpServices={axnRpServices}
                            axnCompanies={axnCompanies}
                            axnAttributeTypes={axnAttributeTypes}
                            axnAttributeTypeFields={axnAttributeTypeFields}
                            policyConfig={policyConfig}
                            policyDecisions={approvePolicyDecisions}
                            attributes={attributes}
                            serviceOfferings={serviceOfferings}
                            acquiredAttributes={acquiredAttributes}
                            unformattedAcquiredAttributes={axnAcquiredAttributes}
                            simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                            axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                            rpServiceID={rpServiceID}
                            workflowID={workflowID}
                            setupPropAndPolicyUpdate={setupPropAndPolicyUpdate}
                            updatePolicyConfig={updatePolicyConfig}
                        />
                        <DecisionEngineDecisionsContainer
                            sectionTitle={"Obligate Rules"}
                            decisionType={PolicyDecisionActionTypes.OBLIGATION}
                            isDisabled={isDisabled}
                            axnuiContent={axnuiContent}
                            isUsingThemedUI={isUsingThemedUI}
                            axnRpServices={axnRpServices}
                            axnCompanies={axnCompanies}
                            axnAttributeTypes={axnAttributeTypes}
                            axnAttributeTypeFields={axnAttributeTypeFields}
                            policyConfig={policyConfig}
                            policyDecisions={obligatePolicyDecisions}
                            attributes={attributes}
                            serviceOfferings={serviceOfferings}
                            acquiredAttributes={acquiredAttributes}
                            unformattedAcquiredAttributes={axnAcquiredAttributes}
                            simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                            axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                            rpServiceID={rpServiceID}
                            workflowID={workflowID}
                            setupPropAndPolicyUpdate={setupPropAndPolicyUpdate}
                            updatePolicyConfig={updatePolicyConfig}
                        />
                        <DefaultDecisionContainer
                            isDisabled={isDisabled}
                            isUsingThemedUI={isUsingThemedUI}
                            axnRpServices={axnRpServices}
                            validationErrors={validationErrors}
                            policyConfig={policyConfig}
                            simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                            rpServiceID={rpServiceID}
                            workflowID={workflowID}
                            updatePolicyConfig={updatePolicyConfig}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                            axnuiContent={axnuiContent}
                        />
                    </ContentPageBody>
                </div>
            </CardContainer>
        </div>
    );
};